<template>
  <v-dialog :retain-focus="false" :value="sectionEditor.show" :width="1000" @input="handleClosePopup">
    <div style="background-color: #fff">
      <div class="header-modal">
        <v-card-title class="d-flex justify-space-between align-center" style="padding: 10px 0">
          <span style="color: #0081c7">{{ fields.title }}</span>
          <v-btn icon @click="handleClosePopup"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
      </div>

      <v-card-text style="max-height: 70vh; overflow-y: auto">
        <div class="mb-4">
          <label class="label-input">Section</label>
          <v-row>
            <v-col cols="12" md="8">
              <v-text-field v-model="fields.title" label="Name" hide-details />
            </v-col>
            <v-col cols="12" md="4">
              <v-checkbox v-model="fields.enableAnchor" label="Enable Anchor" hide-details />
            </v-col>
          </v-row>
        </div>

        <div class="mb-4">
          <SectionSettingsContainerFuild
            :settings="fields.settings"
            :container-page="fields.containerPage"
            :show-background-image="false"
          />
        </div>

        <div class="mb-4">
          <label class="label-input">Header</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.header" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <div class="d-flex justify-space-between align-center mb-2">
            <label class="label-input mb-0">Cards</label>
            <v-btn small text color="primary" @click="handleAddCard">
              <v-icon size="16">mdi-plus</v-icon>
              Add Card
            </v-btn>
          </div>
          <div class="group-item">
            <v-expansion-panels accordion>
              <v-expansion-panel v-for="(card, cardIndex) in fields.cards" :key="`card-${cardIndex}`">
                <v-expansion-panel-header>
                  <div class="d-flex justify-space-between align-center">
                    <span>Card {{ cardIndex + 1 }}: {{ card.title || 'Untitled' }}</span>
                    <v-btn icon @click.stop="handleRemoveCard(cardIndex)">
                      <v-icon size="18" color="#ff6c6c">mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="card.title" label="Title" />
                  <label class="field-label">Image</label>
                  <UploadImage
                    :image="{ imageSrc: card.image }"
                    :height="`180`"
                    :typeUpload="'image'"
                    @callback="obj => handleUploadCardImage(cardIndex, obj)"
                    @callbackDelete="handleRemoveCardImage(cardIndex)"
                  />
                  <v-text-field v-model="card.tag" class="mt-4" label="Tag" />
                  <v-textarea v-model="card.description" label="Description" rows="2" auto-grow />
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </div>
        </div>

        <div class="mb-4">
          <label class="label-input">Recommendation</label>
          <div class="group-item">
            <ckeditor :editor="editor" v-model="fields.recommendation" :config="editorConfig" />
          </div>
        </div>

        <div class="mb-4">
          <ButtonPrimary :button="fields.button" />
        </div>
      </v-card-text>

      <div class="footer-modal">
        <v-card-actions style="padding: 10px 0">
          <v-spacer />
          <v-btn min-width="100" depressed @click="handleClosePopup">Cancel</v-btn>
          <v-btn min-width="100" color="primary" @click="handleSave">Save</v-btn>
        </v-card-actions>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import { ckeditorConfigFullToolbar } from '@/helpers/editor';
import Editor from 'ckeditor5-custom-build/build/ckeditor';
import { updateHtmlFromData } from '../common/html/update.js';
import ButtonPrimary from './common/button-primary.vue';
import SectionSettingsContainerFuild from './common/section-settings-container-fuild.vue';

const DEFAULT_CARD = {
  title: 'Title',
  image: '',
  tag: 'Tag',
  description: 'Description',
};

const DEFAULT_BUTTON = {
  show: true,
  text: 'ADD TO CART',
  link: '#',
  backgroundColor: '#CCF46F',
  color: '#23372B',
  radius: 100,
  iconPrefix: { show: false, src: '' },
  iconSuffix: { show: true, src: '' },
  subBtn: { show: false, text: '' },
};

const clone = value => JSON.parse(JSON.stringify(value));

export default {
  name: 'SofyreUserManualSectionEditor',
  components: { ButtonPrimary, SectionSettingsContainerFuild, UploadImage },
  props: {
    sectionEditor: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      editor: Editor,
      editorConfig: ckeditorConfigFullToolbar,
      fields: {
        title: 'Sofyre User Manual',
        enableAnchor: false,
        settings: {
          paddingTop: true,
          paddingBottom: true,
          backgroundColor: '#F9F9F4',
          textColor: '#23372B',
          overlayColor: '#000000',
          overlayOpacity: 0,
          showOverlay: false,
          backgroundImage: '',
        },
        containerPage: { enableStyle: false, background: '#FFFFFF' },
        header: '',
        cards: [],
        recommendation: '',
        button: clone(DEFAULT_BUTTON),
      },
    };
  },
  mounted() {
    this.applyExtract(this.sectionEditor.sectionExtract);
  },
  watch: {
    sectionEditor: {
      handler(value) {
        if (value && value.sectionExtract) this.applyExtract(value.sectionExtract);
      },
      deep: true,
    },
  },
  methods: {
    applyExtract(extract) {
      if (!extract) return;
      const extractedButton = extract.button || {};
      this.fields = {
        ...this.fields,
        ...extract,
        settings: { ...this.fields.settings, ...(extract.settings || {}) },
        containerPage: { ...this.fields.containerPage, ...(extract.containerPage || {}) },
        cards: Array.isArray(extract.cards) ? clone(extract.cards) : [],
        button: {
          ...this.fields.button,
          ...extractedButton,
          iconPrefix: { ...this.fields.button.iconPrefix, ...(extractedButton.iconPrefix || {}) },
          iconSuffix: { ...this.fields.button.iconSuffix, ...(extractedButton.iconSuffix || {}) },
          subBtn: { ...this.fields.button.subBtn, ...(extractedButton.subBtn || {}) },
        },
      };
    },
    handleAddCard() {
      this.fields.cards.push(clone(DEFAULT_CARD));
    },
    handleRemoveCard(index) {
      this.fields.cards.splice(index, 1);
    },
    handleUploadCardImage(index, obj) {
      this.$set(this.fields.cards[index], 'image', (obj && obj.imageSrc) || '');
    },
    handleRemoveCardImage(index) {
      this.$set(this.fields.cards[index], 'image', '');
    },
    handleClosePopup() {
      this.$emit('close');
    },
    handleSave() {
      const html = updateHtmlFromData.sectionSofyreUserManual(this.sectionEditor.section.html, this.fields);
      this.$emit('update:section', {
        ...this.sectionEditor.section,
        title: this.fields.title,
        html,
      });
      this.$emit('close');
    },
  },
};
</script>

<style scoped lang="scss">
.label-input {
  display: block;
  margin-bottom: 10px;
  color: #000;
  font-size: 18px !important;
  font-weight: 500;
}
.field-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
}
.group-item {
  padding: 15px 10px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
}
.header-modal,
.footer-modal {
  position: sticky;
  left: 0;
  z-index: 999;
  padding: 0 16px;
  background: #fff;
  box-shadow: 0 0 12px #0000000f;
}
.header-modal {
  top: 0;
}
.footer-modal {
  bottom: 0;
}
</style>
